import classnames from 'classnames';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { prettyMoney, scrollTop } from '@/helpers/utils';
import { MoneyField } from '../../UI/MoneyField';
import { RoundButton } from '../../UI/RoundButton';
import { BankTransfer } from './BankTransfer';
import { TotalPaymentAmount } from './TotalPaymentAmount';
import { PaymentMethod } from './PaymentMethod';
import Preloader from '../../UI/Preloader';
import { MyLink } from '../../MyLink';
import { CONTACT_EMAIL } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import { VALIDATION_SCHEMA } from './schema';
import s from './index.module.scss';

const AMOUNT_PRESETS = [50, 100, 300, 500];

const PaymentForm = ({
  onSubmit,
  paymentMethods,
  loading,
  initialValues,
  onBack,
  children,
  disabled,
  className,
}) => {
  const { t } = useTranslation('cash');
  const { profile } = useGetMe();
  const calculateCommission = (amount, method) => {
    if (!method) {
      return 0;
    }
    return (Number(amount) * method.commission) / 100 + method.fixedComission;
  };

  return (
    <Formik
      validationSchema={VALIDATION_SCHEMA(t)}
      initialValues={initialValues}
      onSubmit={(values, formikBag) => {
        formikBag.setFieldValue('hasConfirmed', true);
        onSubmit && onSubmit(values, formikBag);
      }}
    >
      {formikBag => {
        const { values, errors, touched, setFieldValue, handleSubmit } = formikBag;

        const isBankTransfer = values.hasConfirmed && values.method === 'bank';

        return (
          <form className={classnames(s.form, className)} onSubmit={handleSubmit}>
            {isBankTransfer ? (
              <BankTransfer amount={values.amount} />
            ) : (
              <>
                <h1 className={classnames(s.title, 'f-26 f-500')}>
                  {t('payment_form.add_cash_to_your_account')}
                </h1>

                <section className={s.amount}>
                  <h2 className={classnames(s.subtitle, 'f-20 f-500')}>
                    {t('payment_form.select_the_amount_of_your_top_up')}
                  </h2>
                  <div className={s.amount_container}>
                    <div className={s.amount_presets}>
                      {AMOUNT_PRESETS.map((amount, index) => (
                        <div
                          key={index}
                          onClick={() => !disabled && setFieldValue('amount', `${amount}`)}
                          className={classnames(
                            s.amount_preset,
                            Number(values.amount) === amount && 'selected',
                            disabled && 'disabled',
                            'f-20',
                            'f-500',
                          )}
                        >
                          {prettyMoney(amount)}
                        </div>
                      ))}
                    </div>
                    <div className={s.amount_right}>
                      <div className={s.amount_field}>
                        <MoneyField
                          onChange={(_, value) => setFieldValue('amount', value)}
                          name="amount"
                          label={t('payment_form.or_specify_your_own_amount')}
                          value={values.amount}
                          disabled={disabled}
                          error={errors.amount && touched.amount && errors.amount}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                <section className={s.method}>
                  <h2 className={classnames(s.subtitle, 'f-20 f-500')}>
                    {t('payment_form.select_payment_method')}
                  </h2>
                  <div className={s.method_list}>
                    {paymentMethods.map(item => {
                      const commission =
                        item.type === values.method && values.commission
                          ? values.commission
                          : calculateCommission(Number(values.amount), item);

                      return (
                        <PaymentMethod
                          key={item.type}
                          item={item}
                          amount={Number(values.amount)}
                          commission={commission}
                          formikBag={formikBag}
                          disabled={disabled}
                        />
                      );
                    })}
                  </div>
                </section>

                <TotalPaymentAmount
                  amount={Number(values.amount)}
                  method={values.method}
                  setCommission={commission => setFieldValue('commission', commission)}
                />
              </>
            )}

            {children}

            <div className={s.buttons}>
              {(onBack || values.hasConfirmed) && (
                <RoundButton
                  label={t('payment_form.go_back')}
                  className={s.button_back}
                  onClick={() => {
                    if (onBack && !values.hasConfirmed) {
                      return onBack();
                    }
                    scrollTop();
                    setFieldValue('hasConfirmed', false);
                  }}
                />
              )}

              {!isBankTransfer && (
                <RoundButton
                  label={() =>
                    loading ? (
                      <Preloader style={{ height: '24px' }} white />
                    ) : values.method !== 'bank' ? (
                      t('payment_form.go_to_secure_payment')
                    ) : (
                      t('payment_form.go_to_next_step')
                    )
                  }
                  type="submit"
                  onSubmit={handleSubmit}
                  className={s.button_submit}
                  fillBackground
                  fullWidth
                  disabled={disabled || !values.amount || !values.method}
                />
              )}
            </div>

            {values.method !== 'bank' && !values.hasConfirmed && (
              <div
                className={classnames(s.buttons_desc, !onBack && !values.hasConfirmed && 'line')}
              >
                <div />
                <div className="f-12 f-400">
                  {t('payment_form.payment_processing_provided_by_everypay')}
                </div>
              </div>
            )}

            <div className={classnames(s.description, 'f-16-24 f-400')}>
              <div>{t('payment_form.currency_of_your_account')}</div>
              <div>
                {t('payment_form.cash_accepted_only_from_bank_accounts')}
                {profile?.company?.name ? `: ${profile.company.name}.` : '.'}
              </div>
              <div>{t('payment_form.please_note_that_in_some_cases')}</div>
              <div>
                {t('payment_form.transfer_of_funds_depends_on_payment_method')}{' '}
                <MyLink href={APP_LINKS.contactEmail} isExternal underline>
                  {CONTACT_EMAIL}
                </MyLink>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default PaymentForm;
